import type * as mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import type { WhispRiskPart, WhispSiteView } from '../../../model';

export const WHISP_RISK_RED = '#e53935';
export const WHISP_RISK_SOURCE = 'whisp-risk-src';
export const WHISP_RISK_LAYER = 'whisp-risk-line';

export type WhispChipState = 'pending' | 'queued' | 'running' | 'ok' | 'risk' | 'error';

const CHIP_STATES: Record<string, WhispChipState> = {
  PENDING: 'pending',
  QUEUED: 'queued',
  RUNNING: 'running',
  OK: 'ok',
  RISK: 'risk',
  ERROR: 'error',
};

type SettledState = 'ok' | 'risk' | 'error';

const SETTLED_STATES: WhispChipState[] = ['ok', 'risk', 'error'];

export function whispChipState(view: Pick<WhispSiteView, 'status'>): WhispChipState {
  return CHIP_STATES[view.status] ?? 'error';
}

export function isWhispSettled(state: WhispChipState): state is SettledState {
  return SETTLED_STATES.includes(state);
}

function polygonOf(feature: Feature | undefined, sourcePart: number): Polygon | null {
  const geometry = feature?.geometry;
  if (geometry?.type === 'Polygon') return geometry;
  if (geometry?.type !== 'MultiPolygon') return null;
  const coordinates = geometry.coordinates[sourcePart];
  return coordinates ? { type: 'Polygon', coordinates } : null;
}

export function riskPartsToLineFeatures(
  geo: FeatureCollection | undefined,
  riskParts: WhispRiskPart[] | undefined
): Feature<Polygon>[] {
  const features = geo?.features ?? [];
  return (riskParts ?? []).flatMap(({ partId, sourceFeature, sourcePart }) => {
    if (sourceFeature == null) return [];
    const geometry = polygonOf(features[sourceFeature], sourcePart ?? 0);
    return geometry ? [{ type: 'Feature', properties: { partId }, geometry } as Feature<Polygon>] : [];
  });
}

export class WhispRiskLayerController {
  constructor(private readonly map: mapboxgl.Map) {}

  attach(): void {
    this.map.addSource(WHISP_RISK_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    this.map.addLayer({
      id: WHISP_RISK_LAYER,
      type: 'line',
      source: WHISP_RISK_SOURCE,
      paint: { 'line-color': WHISP_RISK_RED, 'line-width': 3 },
    });
  }

  show(features: Feature<Polygon>[]): void {
    const source = this.map.getSource(WHISP_RISK_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    source?.setData({ type: 'FeatureCollection', features });
  }

  setVisible(visible: boolean): void {
    this.map.setLayoutProperty(WHISP_RISK_LAYER, 'visibility', visible ? 'visible' : 'none');
  }
}
